import React from 'react';
import { RotateCcw, ScanEye } from 'lucide-react';
import { getIndomitableProfile, hasStudiedAttacks, STUDIED_ATTACKS_DEFINITION_ID } from '../../../shared/rules/fighter.mjs';
import { useCombatantName } from './CombatantNamesContext';
import { notify } from '../../../utils/notification';

/**
 * Reminder tiles for the Fighter features that work on their own (2024), like the Paladin's passive tiles. Nothing is
 * spent from a tile: Indomitable shows its uses (the reroll is offered when a save fails); Studied Attacks lights while
 * the Fighter has a creature studied after a miss. Clicking one explains it.
 */
export const fighterPassiveTiles = (form, characterId, combatState, nameOf = (id, fallback) => fallback) => {
  const indomitable = getIndomitableProfile(form);
  const studied = (combatState?.activeEffects || []).filter(effect => effect?.definitionId === STUDIED_ATTACKS_DEFINITION_ID
    && String(effect.sourceCombatantId) === String(characterId));
  const names = studied.map(effect => nameOf(effect.targetCombatantId, 'a creature'));
  return [
    indomitable.maximum > 0 && { id: 'indomitable', name: 'Indomitable', Icon: RotateCcw, accent: '#9fd3ff', lit: indomitable.remaining > 0,
      value: `${indomitable.remaining}/${indomitable.maximum}`,
      about: `When you fail a saving throw, reroll it with a +${indomitable.level} bonus (your Fighter level); you must use the new roll. `
        + `The reroll is offered when a save fails. ${indomitable.remaining
          ? `${indomitable.remaining} of ${indomitable.maximum} uses left.` : 'No uses left until you finish a Long Rest.'} Uses return on a Long Rest.` },
    hasStudiedAttacks(form) && { id: 'studied-attacks', name: 'Studied Attacks', Icon: ScanEye, accent: '#ffcf7a', lit: studied.length > 0,
      value: !studied.length ? 'After a miss' : studied.length === 1 ? names[0] : `${studied.length} creatures`,
      about: `When you miss a creature with an attack roll, your next attack roll against it before the end of your next turn has Advantage. ${studied.length
        ? `Studied now: ${names.join(', ')}.` : 'No creature is studied right now.'}` },
  ].filter(Boolean);
};

export default function FighterPassiveTiles({ form, characterId, combatState }) {
  const nameOf = useCombatantName();
  const tiles = fighterPassiveTiles(form, characterId, combatState, nameOf);
  if (!tiles.length) return null;
  return <>{tiles.map(({ id, name, Icon, accent, lit, value, about }) => (
    <button key={id} type="button" data-action-tile={`fighter:${id}`} aria-label={`${name}: ${value}`} title={about}
      className={`combat-hud-resource-tile combat-hud-feature-action-tile combat-hud-resource-tile--passive${lit ? '' : ' combat-hud-resource-tile--dormant'}`}
      style={{ '--hud-resource-accent': accent }} onClick={() => notify(`${name}: ${about}`, 'info')}>
      <span className="combat-hud-resource-tile__icon" aria-hidden="true"><Icon size={20}/></span>
      <span className="combat-hud-resource-tile__name">{name}</span>
      <span className="combat-hud-resource-tile__value">{value}</span>
    </button>
  ))}</>;
}
